import { useEffect, useState } from "react";
import { useOutletContext, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarClock, CheckSquare, Users, FileText, ArrowUpRight, MapPin, Coins, TrendingUp, Pencil, Loader2, Clock, BarChart3, RefreshCw, FileSignature, Receipt, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { StatusBadge } from "@/components/badges";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { EditCompanyDialog } from "./EditCompanyDialog";
import { ValuationRequestDialog } from "./ValuationRequestDialog";
import { formatDate, formatDateTime, nb, nok, meetingTypeLabel } from "@/lib/format";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];

const KPIS = [
  { key: "upcoming_meetings", label: "Kommende møter", icon: CalendarClock, to: "styrerom" },
  { key: "my_open_tasks", label: "Mine åpne oppgaver", icon: CheckSquare, to: "oppgaver" },
  { key: "shareholders", label: "Aksjonærer", icon: Users, to: "aksjehandbok" },
  { key: "documents", label: "Dokumenter", icon: FileText, to: "dokumenter" },
];

export default function Overview() {
  const { company, refetchCompany } = useOutletContext();
  const { user } = useAuth();
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [valDialog, setValDialog] = useState(false);
  const [pendingVal, setPendingVal] = useState(null);
  const [proffFin, setProffFin] = useState(null);
  const [proffFinLoading, setProffFinLoading] = useState(false);
  const [proffFinError, setProffFinError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadProffFin = async (refresh = false) => {
    setProffFinLoading(true);
    if (!refresh) setProffFinError(null);
    try {
      const { data } = await api.get(`/companies/${companyId}/proff-financials`, { params: refresh ? { refresh: true } : {} });
      setProffFin(data);
      setProffFinError(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (refresh && proffFin) toast.error(msg);
      else setProffFinError(msg);
    } finally {
      setProffFinLoading(false);
    }
  };

  useEffect(() => {
    api.get(`/companies/${companyId}/overview`).then((r) => setData(r.data)).catch(() => setData({}));
    api.get(`/companies/${companyId}/valuation-request`).then((r) => setPendingVal(r.data?.pending || null)).catch(() => setPendingVal(null));
  }, [companyId]);

  useEffect(() => {
    if (company?.org_number) loadProffFin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, company?.org_number]);

  const isAdmin = !!user?.is_admin;
  const txBased = company?.valuation_source === "transaction";
  const license = company?.license || {};
  const canEditCompany = isAdmin || (company?.my_roles || []).some((r) => CHAIR_ROLES.includes(r));
  const canEditValuation = canEditCompany;

  const BOARD_ROLE_ORDER = { Styreleder: 0, Nestleder: 1, Styremedlem: 2, Varamedlem: 3 };
  const boardMembers = (company?.board || [])
    .filter((b) => b.role !== "Daglig leder")
    .sort((a, b) => (BOARD_ROLE_ORDER[a.role] ?? 9) - (BOARD_ROLE_ORDER[b.role] ?? 9));
  const dagligLeder = company?.ceo_name || (company?.board || []).find((b) => b.role === "Daglig leder")?.name || "";

  const syncProff = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/sync-proff`);
      toast.success(data.message || "Oppdatert fra Proff");
      await refetchCompany?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const startEditVal = () => setValDialog(true);

  const onValDone = async (res) => {
    if (res?.status === "approved") {
      setPendingVal(null);
      await refetchCompany?.();
    } else if (res?.request) {
      setPendingVal(res.request);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Oversikt</p>
        <h1 className="mt-2 font-headings text-2xl font-extrabold tracking-tight sm:text-3xl">{company?.name}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {company?.industry} · Org.nr {company?.org_number}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KPIS.map((k, i) => (
          <motion.div key={k.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link
              to={`/selskap/${companyId}/${k.to}`}
              data-testid={`kpi-${k.key}`}
              className="group flex flex-col rounded-xl border border-border/60 bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <k.icon className="h-5 w-5 text-primary" strokeWidth={1.7} />
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <div className="mt-4 font-headings text-3xl font-extrabold">{data ? data[k.key] ?? 0 : <Skeleton className="h-8 w-10" />}</div>
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Next meeting */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm">
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Neste møte</h3>
            {data?.next_meeting ? (
              <Link to={`/selskap/${companyId}/moter/${data.next_meeting.id}`} className="group mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="font-headings text-lg font-bold tracking-tight group-hover:text-primary">{data.next_meeting.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{meetingTypeLabel(data.next_meeting.type)}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> {formatDateTime(data.next_meeting.date)}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {data.next_meeting.location}</span>
                  </div>
                </div>
                <StatusBadge status={data.next_meeting.status} />
              </Link>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">Ingen planlagte møter.</p>
            )}
          </div>

          <div className="mt-6 rounded-xl border border-border/60 bg-card p-6 shadow-sm">
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Siste møter</h3>
            <div className="mt-3 divide-y divide-border/60">
              {(data?.recent_meetings || []).map((m) => (
                <Link key={m.id} to={`/selskap/${companyId}/moter/${m.id}`} className="flex items-center justify-between gap-3 py-3 transition-colors hover:text-primary">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{m.title}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(m.date)} · {meetingTypeLabel(m.type)}</p>
                  </div>
                  <StatusBadge status={m.status} />
                </Link>
              ))}
              {data && (!data.recent_meetings || data.recent_meetings.length === 0) ? (
                <p className="py-3 text-sm text-muted-foreground">Ingen møter ennå.</p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Company info */}
        <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
            <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Selskapsinfo</h3>
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {canEditCompany && company?.org_number ? (
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 whitespace-nowrap" data-testid="sync-proff-overview-btn" onClick={syncProff} disabled={syncing} title="Hent fersk styreinfo og adresse fra Proff">
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Oppdater fra Proff
                </Button>
              ) : null}
              {canEditCompany ? <EditCompanyDialog company={company} onSaved={refetchCompany} /> : null}
            </div>
          </div>
          <dl className="mt-4 space-y-3 text-sm">
            {[
              ["Foretaksform", company?.org_form],
              ["Stiftet", company?.founded],
              ["Bransje", company?.industry],
              ["Adresse", `${company?.address}, ${company?.postal_code} ${company?.city}`],
            ].map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-4">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-medium">{v}</dd>
              </div>
            ))}
            {boardMembers.length ? (
              <div className="border-t border-border/60 pt-3" data-testid="company-board-section">
                <dt className="mb-2 text-muted-foreground">Styret</dt>
                <div className="space-y-1.5" data-testid="company-board-list">
                  {boardMembers.map((b, i) => (
                    <div key={i} data-testid={`company-board-${i}`} className="flex items-center justify-between gap-3">
                      <span className="font-medium">{b.name}</span>
                      <span className="text-xs text-muted-foreground">{b.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : company?.chair_name ? (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-muted-foreground">Styreleder</dt>
                <dd className="text-right font-medium">{company.chair_name}</dd>
              </div>
            ) : null}
            {dagligLeder ? (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-muted-foreground">Daglig leder</dt>
                <dd className="text-right font-medium">{dagligLeder}</dd>
              </div>
            ) : null}
            <div className="flex items-start justify-between gap-4 border-t border-border/60 pt-3">
              <dt className="flex items-center gap-1.5 text-muted-foreground"><Coins className="h-4 w-4" /> Aksjekapital</dt>
              <dd className="text-right font-semibold">{nok(company?.share_capital)}</dd>
            </div>
            <div className="flex items-start justify-between gap-4">
              <dt className="text-muted-foreground">Antall aksjer</dt>
              <dd className="text-right font-semibold">{nb(company?.total_shares)}</dd>
            </div>
            <div className="flex items-start justify-between gap-4 border-t border-border/60 pt-3">
              <dt className="flex items-center gap-1.5 text-muted-foreground"><TrendingUp className="h-4 w-4" /> Verdsettelse</dt>
              <dd className="flex items-center gap-1.5 text-right font-semibold" data-testid="valuation-display">
                {company?.valuation ? nok(company.valuation) : <span className="text-muted-foreground font-normal">Ikke satt</span>}
                {canEditValuation ? (
                  <button data-testid="valuation-edit-btn" onClick={startEditVal} className="text-muted-foreground transition-colors hover:text-primary" title={isAdmin ? "Oppdater verdsettelse" : "Foreslå verdsettelse"}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </dd>
            </div>
            {pendingVal ? (
              <div className="flex items-start gap-1.5 rounded-md bg-amber-500/10 px-2.5 py-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400" data-testid="valuation-pending">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Forslag til godkjenning: {nok(pendingVal.valuation)} (venter på admin)
                  {pendingVal.attachment ? " · dokumentasjon vedlagt" : ""}
                </span>
              </div>
            ) : null}
            {txBased ? (
              <p className="text-[11px] leading-snug text-muted-foreground" data-testid="valuation-tx-note">
                Verdsettelsen beregnes nå automatisk fra siste aksjetransaksjon. Du kan likevel foreslå en oppdatert verdsettelse med dokumentasjon – admin godkjenner og overstyrer.
              </p>
            ) : canEditValuation ? (
              <p className="text-[11px] leading-snug text-muted-foreground">
                {isAdmin
                  ? "Sett verdsettelsen manuelt. Etter første aksjetransaksjon beregnes den automatisk – du kan fortsatt overstyre den her."
                  : "Foreslå en verdsettelse med dokumentasjon – admin må godkjenne. Etter første aksjetransaksjon beregnes den automatisk, men du kan fortsatt foreslå en oppdatering."}
              </p>
            ) : null}
          </dl>
        </div>
      </div>

      {/* Lisens & faktura */}
      <div className="mt-6 rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="license-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <FileSignature className="h-4 w-4 text-primary" /> Lisens & faktura
          </h3>
          {license.signed ? (
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid="license-status-signed">
              <CheckCircle2 className="h-3.5 w-3.5" /> Signert
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid="license-status-unsigned">
              <Clock className="h-3.5 w-3.5" /> Ikke signert
            </span>
          )}
        </div>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Årlig lisens</dt>
            <dd className="mt-0.5 font-semibold" data-testid="license-price">{nok(license.price_nok)} <span className="text-xs font-normal text-muted-foreground">eks. mva.</span></dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Fornyelsesdato</dt>
            <dd className="mt-0.5 font-semibold" data-testid="license-renewal">{formatDate(license.renewal_date)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Betalingsstatus</dt>
            <dd className="mt-0.5">
              {license.payment_status === "paid" ? (
                <span className="font-semibold text-emerald-600 dark:text-emerald-400" data-testid="license-payment">Betalt</span>
              ) : (
                <span className="font-semibold text-amber-600 dark:text-amber-400" data-testid="license-payment">Ikke betalt</span>
              )}
            </dd>
          </div>
        </dl>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/60 pt-4">
          <Button variant="outline" size="sm" className="gap-2" data-testid="license-card-agreement"
            onClick={() => openSigned(`/companies/${companyId}/license/agreement-link`).catch(() => toast.error("Kunne ikke åpne avtalen"))}>
            <FileSignature className="h-4 w-4" /> Lisensavtale (PDF)
          </Button>
          <Button variant="outline" size="sm" className="gap-2" data-testid="license-card-invoice"
            onClick={() => openSigned(`/companies/${companyId}/license/invoice-link`).catch(() => toast.error("Kunne ikke åpne fakturaen"))}>
            <Receipt className="h-4 w-4" /> Faktura (PDF)
          </Button>
        </div>
        <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
          Faktura utstedes av Murphy AS. Betaling registreres manuelt – ta kontakt med Murphy AS ved spørsmål.
        </p>
      </div>

      {/* Regnskapstall fra Proff */}
      <div className="mt-6 rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="proff-financials-card">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <BarChart3 className="h-4 w-4 text-primary" /> Regnskapstall fra Proff
          </h3>
          {proffFin ? (
            <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => loadProffFin(true)} disabled={proffFinLoading} data-testid="proff-fin-refresh">
              {proffFinLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Oppdater
            </Button>
          ) : null}
        </div>
        {!company?.org_number ? (
          <p className="mt-4 text-sm text-muted-foreground">Legg til organisasjonsnummer på selskapet for å hente regnskapstall fra Proff.</p>
        ) : proffFin ? (
          <div className="mt-4 overflow-x-auto" data-testid="proff-fin-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 text-left font-semibold">Post</th>
                  {proffFin.years.map((y) => <th key={y} className="py-2 text-right font-semibold">{y}</th>)}
                </tr>
              </thead>
              <tbody>
                {proffFin.rows.map((row) => (
                  <tr key={row.code} className="border-b border-border/40 last:border-0">
                    <td className="py-2 pr-3 text-muted-foreground">{row.label}</td>
                    {proffFin.years.map((y) => (
                      <td key={y} className="py-2 text-right font-medium tabular-nums">
                        {row.values[y] == null ? "–" : row.kind === "pct" ? `${row.values[y]} %` : nok(row.values[y])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-[11px] text-muted-foreground" data-testid="proff-fin-updated">
              Kilde: Proff · beløp i hele kroner{proffFin.fetched_at ? ` · sist oppdatert ${formatDateTime(proffFin.fetched_at)}` : ""}.
            </p>
          </div>
        ) : proffFinLoading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Henter fra Proff …</div>
        ) : proffFinError ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" data-testid="proff-fin-error">{proffFinError}</p>
            <Button variant="outline" size="sm" onClick={() => loadProffFin(true)}>Prøv igjen</Button>
          </div>
        ) : null}
      </div>

      {canEditValuation ? (
        <ValuationRequestDialog
          companyId={companyId}
          currentValuation={company?.valuation}
          isAdmin={isAdmin}
          open={valDialog}
          onOpenChange={setValDialog}
          onDone={onValDone}
        />
      ) : null}
    </div>
  );
}
